import type { Player } from '../types/player';
import type { LeagueFormatType } from '../components/LeagueFormatSelector';
import type { ClassifiedTradeBalance } from './tradeBalance';

export type ScoringFormat = 'standard' | 'ppr' | 'half-ppr';

export const REPLACEMENT_RANKS: Record<string, Record<string, number>> = {
  standard: { QB: 15, RB: 28, WR: 32, TE: 14, K: 12, DEF: 12 },
  superflex: { QB: 22, RB: 26, WR: 30, TE: 12, K: 12, DEF: 12 },
  '2qb': { QB: 24, RB: 24, WR: 28, TE: 12, K: 12, DEF: 12 },
  bestball: { QB: 20, RB: 36, WR: 44, TE: 16, K: 0, DEF: 0 },
  dynasty: { QB: 18, RB: 26, WR: 34, TE: 14, K: 0, DEF: 0 },
};

export function estimatedReceptions(player: Player): number {
  const targets = player.playerStats?.targets ?? 0;
  if (targets <= 0) return 0;
  if (player.position === 'TE') return targets * 0.68;
  if (player.position === 'WR') return targets * 0.62;
  if (player.position === 'RB') return targets * 0.75;
  return 0;
}

export function scoringReceptionBonus(player: Player, scoring: ScoringFormat): number {
  if (scoring === 'standard') return 0;
  const receptions = estimatedReceptions(player);
  return scoring === 'ppr' ? receptions : receptions * 0.5;
}

export function ordinal(n: number): string {
  const abs = Math.abs(n);
  const mod100 = abs % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (abs % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

function groupByPosition(players: Player[]): Record<string, Player[]> {
  const groups: Record<string, Player[]> = {};
  for (const player of players) {
    const pos = player.position;
    if (!groups[pos]) groups[pos] = [];
    groups[pos].push(player);
  }
  return groups;
}

export function buildPlayerInsight(
  player: Player,
  positionRank: number,
  vorp: number,
  scoring: ScoringFormat,
  format: LeagueFormatType
): string {
  const bits: string[] = [];
  const receptions = estimatedReceptions(player);
  if (scoring !== 'standard' && receptions >= 80) {
    bits.push(scoring === 'ppr' ? 'PPR volume' : 'half PPR volume');
  }

  if ((format === 'superflex' || format === '2qb') && player.position === 'QB' && positionRank <= 14) {
    bits.push(format === '2qb' ? 'must-start in 2QB' : 'superflex starter');
  }

  if (player.position === 'RB' && (player.age ?? 0) >= 31) {
    bits.push('age risk');
  } else if ((player.age ?? 99) <= 24 && (player.position === 'WR' || player.position === 'RB' || player.position === 'TE')) {
    bits.push('still ascending');
  }

  if (vorp < 5 && positionRank > 12) {
    bits.push('near replacement');
  }

  return bits.join(' · ');
}

export function applyLeagueInsights(
  players: Player[],
  format: LeagueFormatType,
  scoring: ScoringFormat
): Player[] {
  const withScoring = players.map((player, index) => {
    const bonus = scoringReceptionBonus(player, scoring);
    const engineRank = player.rank && player.rank > 0 ? player.rank : index + 1;
    const base = player.value ?? player.projection ?? player.projectedPoints ?? 0;
    const value = base + bonus * 0.35;
    // Keep the format engine's order. Scoring only nudges nearby players.
    const sortKey = engineRank - bonus / 28;
    return {
      ...player,
      value,
      formatAdjustedValue: value,
      sortKey,
    };
  });

  const sorted = [...withScoring].sort((a, b) => (a as Player & { sortKey: number }).sortKey - (b as Player & { sortKey: number }).sortKey);
  const byPos = groupByPosition(sorted);
  const replacement = REPLACEMENT_RANKS[format] || REPLACEMENT_RANKS.standard;
  const replacementScore: Record<string, number> = {};

  for (const [pos, list] of Object.entries(byPos)) {
    const rank = replacement[pos] || 12;
    const idx = Math.max(0, rank - 1);
    replacementScore[pos] = list[idx]?.value ?? list[list.length - 1]?.value ?? 0;
  }

  const posCount: Record<string, number> = {};
  return sorted.map((player, index) => {
    const positionRank = (posCount[player.position] || 0) + 1;
    posCount[player.position] = positionRank;
    const vorp = Math.max(0, (player.value || 0) - (replacementScore[player.position] || 0));
    const tier =
      positionRank <= 12
        ? `${player.position}1`
        : positionRank <= 24
          ? `${player.position}2`
          : `${player.position}3+`;
    return {
      ...player,
      rank: index + 1,
      positionRank,
      vorp,
      tier,
      insight: buildPlayerInsight(player, positionRank, vorp, scoring, format),
    };
  });
}

export function boardInsightSummary(format: LeagueFormatType, scoring: ScoringFormat): string {
  const scoringNote =
    scoring === 'ppr'
      ? 'Full PPR lifts high-target WRs and TEs.'
      : scoring === 'half-ppr'
        ? 'Half PPR still rewards volume, just less than full PPR.'
        : 'Standard scoring leans on yards and touchdowns.';

  if (format === 'superflex' || format === '2qb') {
    return `QB scarcity is the whole game in ${format === '2qb' ? '2QB' : 'superflex'}. ${scoringNote}`;
  }
  if (format === 'dynasty') {
    return `Dynasty pays youth more than a redraft board. ${scoringNote}`;
  }
  if (format === 'bestball') {
    return `Best ball rewards ceiling and roster construction over weekly start decisions. ${scoringNote}`;
  }
  return `Redraft value is about beating replacement at scarce spots. ${scoringNote}`;
}

export interface TradeExplanation {
  headline: string;
  detail: string;
}

export function explainTrade(
  yourPlayers: Player[],
  theirPlayers: Player[],
  classified: ClassifiedTradeBalance,
  _ewa?: number
): TradeExplanation {
  if (yourPlayers.length === 0 && theirPlayers.length === 0) {
    return {
      headline: 'Add players to both sides',
      detail: 'The board will show who is giving up more starter value.',
    };
  }

  const yourBest = [...yourPlayers].sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999))[0];
  const theirBest = [...theirPlayers].sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999))[0];

  if (classified.balance === 'fair') {
    return {
      headline: 'This trade is close',
      detail: `${yourBest ? `${yourBest.name} for ` : ''}${theirBest ? theirBest.name : 'their side'} is roughly even on this board.`,
    };
  }

  if (classified.winner === 'your-team') {
    return {
      headline: classified.balance === 'lopsided' ? 'You win this one easily' : 'Slight edge to you',
      detail: theirBest
        ? `${theirBest.name} is more than you are sending out.`
        : 'You are adding more board value than you are sending out.',
    };
  }

  if (classified.winner === 'their-team') {
    return {
      headline: classified.balance === 'lopsided' ? 'They win this one easily' : 'Slight edge to them',
      detail: yourBest
        ? `${yourBest.name} is more than you are getting back.`
        : 'You are sending more board value than you are getting back.',
    };
  }

  return {
    headline: 'This trade is close',
    detail: 'Both sides are near even after format and scoring.',
  };
}
